/**
 * FEA Solver
 * 
 * Finite Element Analysis solver for structural analysis.
 * Implements Direct Stiffness Method with Gauss elimination.
 */

import {
    AnalyticalModelManager,
    AnalyticalNode,
    AnalyticalMember
} from './AnalyticalModelManager';
import {
    StructuralLoadManager,
    PointLoad,
    DistributedLoad
} from './StructuralLoadManager';
import * as THREE from 'three';

// ==================== Types ====================

export interface AnalysisResults {
    displacements: Map<string, NodeDisplacement>;
    reactions: Map<string, NodeReaction>;
    memberForces: Map<string, MemberForces>;
    maxDisplacement: number;
    maxStress: number;
    isConverged: boolean;
    analysisTime: number;
}

export interface NodeDisplacement {
    nodeId: string;
    dx: number;  // m
    dy: number;  // m
    dz: number;  // m
    rx: number;  // rad
    ry: number;  // rad
    rz: number;  // rad
}

export interface NodeReaction {
    nodeId: string;
    fx: number;  // kN
    fy: number;  // kN
    fz: number;  // kN
    mx: number;  // kN·m
    my: number;  // kN·m
    mz: number;  // kN·m
}

export interface MemberForces {
    memberId: string;
    stations: number[];  // 0 to 1 along member
    axialForce: number[];
    shearY: number[];
    shearZ: number[];
    momentY: number[];
    momentZ: number[];
    torsion: number[];
    stress: number[];
}

// ==================== Matrix Operations ====================

class Matrix {
    data: number[][];
    rows: number;
    cols: number;

    constructor(rows: number, cols: number, fill: number = 0) {
        this.rows = rows;
        this.cols = cols;
        this.data = Array(rows).fill(null).map(() => Array(cols).fill(fill));
    }

    static identity(size: number): Matrix {
        const m = new Matrix(size, size);
        for (let i = 0; i < size; i++) m.data[i][i] = 1;
        return m;
    }

    get(row: number, col: number): number {
        return this.data[row][col];
    }

    set(row: number, col: number, value: number): void {
        this.data[row][col] = value;
    }

    add(other: Matrix): Matrix {
        const result = new Matrix(this.rows, this.cols);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.data[i][j] = this.data[i][j] + other.data[i][j];
            }
        }
        return result;
    }

    multiply(other: Matrix): Matrix {
        const result = new Matrix(this.rows, other.cols);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < other.cols; j++) {
                let sum = 0;
                for (let k = 0; k < this.cols; k++) {
                    sum += this.data[i][k] * other.data[k][j];
                }
                result.data[i][j] = sum;
            }
        }
        return result;
    }

    transpose(): Matrix {
        const result = new Matrix(this.cols, this.rows);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.data[j][i] = this.data[i][j];
            }
        }
        return result;
    }

    addToSubmatrix(startRow: number, startCol: number, submatrix: Matrix): void {
        for (let i = 0; i < submatrix.rows; i++) {
            for (let j = 0; j < submatrix.cols; j++) {
                this.data[startRow + i][startCol + j] += submatrix.data[i][j];
            }
        }
    }
}

// ==================== Vector Operations ====================

class Vector {
    data: number[];
    size: number;

    constructor(size: number, fill: number = 0) {
        this.size = size;
        this.data = Array(size).fill(fill);
    }

    get(index: number): number {
        return this.data[index];
    }

    set(index: number, value: number): void {
        this.data[index] = value;
    }

    add(other: Vector): Vector {
        const result = new Vector(this.size);
        for (let i = 0; i < this.size; i++) {
            result.data[i] = this.data[i] + other.data[i];
        }
        return result;
    }

    scale(factor: number): Vector {
        const result = new Vector(this.size);
        for (let i = 0; i < this.size; i++) {
            result.data[i] = this.data[i] * factor;
        }
        return result;
    }

    norm(): number {
        let sum = 0;
        for (let i = 0; i < this.size; i++) {
            sum += this.data[i] * this.data[i];
        }
        return Math.sqrt(sum);
    }
}

// ==================== Gauss Elimination Solver ====================

function gaussElimination(K: Matrix, F: Vector): Vector | null {
    const n = K.rows;
    const A = new Matrix(n, n + 1);

    // Augmented matrix [K | F]
    for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
            A.set(i, j, K.get(i, j));
        }
        A.set(i, n, F.get(i));
    }

    // Forward elimination with partial pivoting
    for (let k = 0; k < n; k++) {
        // Find pivot
        let maxVal = Math.abs(A.get(k, k));
        let maxRow = k;
        for (let i = k + 1; i < n; i++) {
            if (Math.abs(A.get(i, k)) > maxVal) {
                maxVal = Math.abs(A.get(i, k));
                maxRow = i;
            }
        }

        // Swap rows
        if (maxRow !== k) {
            for (let j = k; j <= n; j++) {
                const temp = A.get(k, j);
                A.set(k, j, A.get(maxRow, j));
                A.set(maxRow, j, temp);
            }
        }

        // Check for singular matrix
        if (Math.abs(A.get(k, k)) < 1e-12) {
            console.error('Singular matrix - no unique solution');
            return null;
        }

        // Eliminate column
        for (let i = k + 1; i < n; i++) {
            const factor = A.get(i, k) / A.get(k, k);
            for (let j = k; j <= n; j++) {
                A.set(i, j, A.get(i, j) - factor * A.get(k, j));
            }
        }
    }

    // Back substitution
    const x = new Vector(n);
    for (let i = n - 1; i >= 0; i--) {
        let sum = A.get(i, n);
        for (let j = i + 1; j < n; j++) {
            sum -= A.get(i, j) * x.get(j);
        }
        x.set(i, sum / A.get(i, i));
    }

    return x;
}

// ==================== FEA Solver Class ====================

export class FEASolver {
    private modelManager: AnalyticalModelManager;
    private loadManager: StructuralLoadManager;
    private nodeIndexMap: Map<string, number> = new Map();
    private DOF_PER_NODE = 6;  // 3D frame: 6 DOF per node

    constructor(modelManager: AnalyticalModelManager, loadManager: StructuralLoadManager) {
        this.modelManager = modelManager;
        this.loadManager = loadManager;
    }

    public analyze(loadCombinationId: string): AnalysisResults {
        const startTime = performance.now();

        // 1. Validate model
        if (!this.modelManager.validateModel()) {
            return this.createErrorResult('Model validation failed: ' + this.modelManager.getErrorMessages().join(', '));
        }

        const nodes = this.modelManager.getNodes();
        const members = this.modelManager.getMembers();
        const numNodes = nodes.length;
        const totalDOF = numNodes * this.DOF_PER_NODE;

        // 2. Create node index mapping
        this.nodeIndexMap.clear();
        nodes.forEach((node, index) => {
            this.nodeIndexMap.set(node.id, index);
        });

        // 3. Assemble global stiffness matrix
        const K = this.assembleGlobalStiffnessMatrix(members, totalDOF);

        // 4. Assemble load vector
        const combinedLoads = this.loadManager.calculateCombinedLoads(loadCombinationId);
        const F = this.assembleLoadVector(combinedLoads.pointLoads, combinedLoads.distributedLoads, totalDOF, members);

        // 5. Apply boundary conditions
        const { Kreduced, Freduced, freeDOFs, fixedDOFs } = this.applyBoundaryConditions(K, F, nodes);

        // 6. Solve system
        const solution = gaussElimination(Kreduced, Freduced);
        if (!solution) {
            return this.createErrorResult('Solver failed: Matrix is singular');
        }

        // 7. Extract results
        const displacements = this.extractDisplacements(solution, freeDOFs, nodes);
        const reactions = this.calculateReactions(K, displacements, F, fixedDOFs, nodes);
        const memberForces = this.calculateMemberForces(members, displacements);

        // 8. Calculate summary statistics
        let maxDisplacement = 0;
        let maxStress = 0;

        for (const disp of displacements.values()) {
            const magnitude = Math.sqrt(disp.dx ** 2 + disp.dy ** 2 + disp.dz ** 2);
            maxDisplacement = Math.max(maxDisplacement, magnitude);
        }

        for (const forces of memberForces.values()) {
            for (const stress of forces.stress) {
                maxStress = Math.max(maxStress, Math.abs(stress));
            }
        }

        const endTime = performance.now();

        return {
            displacements,
            reactions,
            memberForces,
            maxDisplacement,
            maxStress,
            isConverged: true,
            analysisTime: endTime - startTime
        };
    }

    private assembleGlobalStiffnessMatrix(members: AnalyticalMember[], totalDOF: number): Matrix {
        const K = new Matrix(totalDOF, totalDOF);

        for (const member of members) {
            const startIndex = this.nodeIndexMap.get(member.startNodeId)!;
            const endIndex = this.nodeIndexMap.get(member.endNodeId)!;

            const startNode = this.modelManager.getModel().nodes.get(member.startNodeId)!;
            const endNode = this.modelManager.getModel().nodes.get(member.endNodeId)!;

            // Calculate member length and direction
            const start = new THREE.Vector3(...startNode.position);
            const end = new THREE.Vector3(...endNode.position);
            const length = start.distanceTo(end);

            // Get local stiffness matrix
            const kLocal = this.getLocalStiffnessMatrix(member, length);

            // Transformation matrix
            const T = this.getTransformationMatrix(start, end);

            // Transform to global: K_global = T^T * K_local * T
            const kGlobal = T.transpose().multiply(kLocal).multiply(T);

            // Assemble into global matrix
            const indices = [
                startIndex * this.DOF_PER_NODE,
                endIndex * this.DOF_PER_NODE
            ];

            for (let i = 0; i < 2; i++) {
                for (let j = 0; j < 2; j++) {
                    for (let di = 0; di < this.DOF_PER_NODE; di++) {
                        for (let dj = 0; dj < this.DOF_PER_NODE; dj++) {
                            const globalI = indices[i] + di;
                            const globalJ = indices[j] + dj;
                            const localI = i * this.DOF_PER_NODE + di;
                            const localJ = j * this.DOF_PER_NODE + dj;
                            K.set(globalI, globalJ, K.get(globalI, globalJ) + kGlobal.get(localI, localJ));
                        }
                    }
                }
            }
        }

        return K;
    }

    private getLocalStiffnessMatrix(member: AnalyticalMember, L: number): Matrix {
        const E = member.material.youngsModulus;
        const G = member.material.shearModulus;
        const A = member.section.area;
        const Ix = member.section.momentOfInertiaX;
        const Iy = member.section.momentOfInertiaY;
        const Iz = member.section.momentOfInertiaZ;

        const k = new Matrix(12, 12);

        // Axial stiffness
        const EA_L = E * A / L;
        k.set(0, 0, EA_L);
        k.set(0, 6, -EA_L);
        k.set(6, 0, -EA_L);
        k.set(6, 6, EA_L);

        // Torsional stiffness
        const GIz_L = G * Iz / L;
        k.set(3, 3, GIz_L);
        k.set(3, 9, -GIz_L);
        k.set(9, 3, -GIz_L);
        k.set(9, 9, GIz_L);

        // Bending about Y-axis
        const EIy = E * Iy;
        const L2 = L * L;
        const L3 = L2 * L;

        k.set(2, 2, 12 * EIy / L3);
        k.set(2, 4, 6 * EIy / L2);
        k.set(2, 8, -12 * EIy / L3);
        k.set(2, 10, 6 * EIy / L2);
        k.set(4, 2, 6 * EIy / L2);
        k.set(4, 4, 4 * EIy / L);
        k.set(4, 8, -6 * EIy / L2);
        k.set(4, 10, 2 * EIy / L);
        k.set(8, 2, -12 * EIy / L3);
        k.set(8, 4, -6 * EIy / L2);
        k.set(8, 8, 12 * EIy / L3);
        k.set(8, 10, -6 * EIy / L2);
        k.set(10, 2, 6 * EIy / L2);
        k.set(10, 4, 2 * EIy / L);
        k.set(10, 8, -6 * EIy / L2);
        k.set(10, 10, 4 * EIy / L);

        // Bending about X-axis
        const EIx = E * Ix;
        k.set(1, 1, 12 * EIx / L3);
        k.set(1, 5, -6 * EIx / L2);
        k.set(1, 7, -12 * EIx / L3);
        k.set(1, 11, -6 * EIx / L2);
        k.set(5, 1, -6 * EIx / L2);
        k.set(5, 5, 4 * EIx / L);
        k.set(5, 7, 6 * EIx / L2);
        k.set(5, 11, 2 * EIx / L);
        k.set(7, 1, -12 * EIx / L3);
        k.set(7, 5, 6 * EIx / L2);
        k.set(7, 7, 12 * EIx / L3);
        k.set(7, 11, 6 * EIx / L2);
        k.set(11, 1, -6 * EIx / L2);
        k.set(11, 5, 2 * EIx / L);
        k.set(11, 7, 6 * EIx / L2);
        k.set(11, 11, 4 * EIx / L);

        return k;
    }

    private getTransformationMatrix(start: THREE.Vector3, end: THREE.Vector3): Matrix {
        const T = new Matrix(12, 12);

        // Direction cosines
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const dz = end.z - start.z;
        const L = Math.sqrt(dx * dx + dy * dy + dz * dz);

        const cx = dx / L;
        const cy = dy / L;
        const cz = dz / L;

        // Rotation submatrix (3x3)
        const r = new Matrix(3, 3);

        if (Math.abs(cz) < 0.999) {
            // General case
            const D = Math.sqrt(cx * cx + cy * cy);
            r.set(0, 0, cx);
            r.set(0, 1, cy);
            r.set(0, 2, cz);
            r.set(1, 0, -cy / D);
            r.set(1, 1, cx / D);
            r.set(1, 2, 0);
            r.set(2, 0, -cx * cz / D);
            r.set(2, 1, -cy * cz / D);
            r.set(2, 2, D);
        } else {
            // Vertical member
            r.set(0, 0, 0);
            r.set(0, 1, 0);
            r.set(0, 2, cz > 0 ? 1 : -1);
            r.set(1, 0, 0);
            r.set(1, 1, 1);
            r.set(1, 2, 0);
            r.set(2, 0, cz > 0 ? -1 : 1);
            r.set(2, 1, 0);
            r.set(2, 2, 0);
        }

        // Build 12x12 transformation matrix
        for (let block = 0; block < 4; block++) {
            for (let i = 0; i < 3; i++) {
                for (let j = 0; j < 3; j++) {
                    T.set(block * 3 + i, block * 3 + j, r.get(i, j));
                }
            }
        }

        return T;
    }

    private assembleLoadVector(
        pointLoads: PointLoad[],
        distLoads: DistributedLoad[],
        totalDOF: number,
        members: AnalyticalMember[]
    ): Vector {
        const F = new Vector(totalDOF);

        // Point loads
        for (const pl of pointLoads) {
            const nodeIndex = this.nodeIndexMap.get(pl.nodeId);
            if (nodeIndex === undefined) continue;

            const baseIndex = nodeIndex * this.DOF_PER_NODE;
            F.set(baseIndex + 0, F.get(baseIndex + 0) + pl.fx * 1000);  // kN to N
            F.set(baseIndex + 1, F.get(baseIndex + 1) + pl.fy * 1000);
            F.set(baseIndex + 2, F.get(baseIndex + 2) + pl.fz * 1000);
            F.set(baseIndex + 3, F.get(baseIndex + 3) + pl.mx * 1000);
            F.set(baseIndex + 4, F.get(baseIndex + 4) + pl.my * 1000);
            F.set(baseIndex + 5, F.get(baseIndex + 5) + pl.mz * 1000);
        }

        // Distributed loads -> Equivalent nodal loads
        for (const dl of distLoads) {
            const member = members.find(m => m.id === dl.memberId || m.elementId === dl.memberId);
            if (!member) continue;

            const startNode = this.modelManager.getModel().nodes.get(member.startNodeId)!;
            const endNode = this.modelManager.getModel().nodes.get(member.endNodeId)!;
            const L = new THREE.Vector3(...startNode.position).distanceTo(new THREE.Vector3(...endNode.position));

            // Uniform load: F = w*L/2 at each end, M = w*L²/12 at fixed ends
            const w = (dl.startMagnitude + dl.endMagnitude) / 2 * 1000;  // kN/m to N/m
            const F_end = w * L / 2;

            const startIndex = this.nodeIndexMap.get(member.startNodeId)! * this.DOF_PER_NODE;
            const endIndex = this.nodeIndexMap.get(member.endNodeId)! * this.DOF_PER_NODE;

            // Apply in Z direction (gravity)
            F.set(startIndex + 2, F.get(startIndex + 2) - F_end);
            F.set(endIndex + 2, F.get(endIndex + 2) - F_end);
        }

        return F;
    }

    private applyBoundaryConditions(K: Matrix, F: Vector, nodes: AnalyticalNode[]): {
        Kreduced: Matrix;
        Freduced: Vector;
        freeDOFs: number[];
        fixedDOFs: number[];
    } {
        const freeDOFs: number[] = [];
        const fixedDOFs: number[] = [];

        for (const node of nodes) {
            const baseIndex = this.nodeIndexMap.get(node.id)! * this.DOF_PER_NODE;

            switch (node.boundaryCondition) {
                case 'Fixed':
                    for (let i = 0; i < 6; i++) fixedDOFs.push(baseIndex + i);
                    break;
                case 'Pinned':
                    for (let i = 0; i < 3; i++) fixedDOFs.push(baseIndex + i);
                    for (let i = 3; i < 6; i++) freeDOFs.push(baseIndex + i);
                    break;
                case 'Roller':
                    fixedDOFs.push(baseIndex + 2);  // Fixed in Z
                    for (let i = 0; i < 6; i++) {
                        if (i !== 2) freeDOFs.push(baseIndex + i);
                    }
                    break;
                default:
                    for (let i = 0; i < 6; i++) freeDOFs.push(baseIndex + i);
            }
        }

        // Build reduced system
        const n = freeDOFs.length;
        const Kreduced = new Matrix(n, n);
        const Freduced = new Vector(n);

        for (let i = 0; i < n; i++) {
            Freduced.set(i, F.get(freeDOFs[i]));
            for (let j = 0; j < n; j++) {
                Kreduced.set(i, j, K.get(freeDOFs[i], freeDOFs[j]));
            }
        }

        return { Kreduced, Freduced, freeDOFs, fixedDOFs };
    }

    private extractDisplacements(solution: Vector, freeDOFs: number[], nodes: AnalyticalNode[]): Map<string, NodeDisplacement> {
        const displacements = new Map<string, NodeDisplacement>();
        const fullDisp = new Vector(nodes.length * this.DOF_PER_NODE);

        for (let i = 0; i < freeDOFs.length; i++) {
            fullDisp.set(freeDOFs[i], solution.get(i));
        }

        for (const node of nodes) {
            const baseIndex = this.nodeIndexMap.get(node.id)! * this.DOF_PER_NODE;
            displacements.set(node.id, {
                nodeId: node.id,
                dx: fullDisp.get(baseIndex + 0),
                dy: fullDisp.get(baseIndex + 1),
                dz: fullDisp.get(baseIndex + 2),
                rx: fullDisp.get(baseIndex + 3),
                ry: fullDisp.get(baseIndex + 4),
                rz: fullDisp.get(baseIndex + 5)
            });
        }

        return displacements;
    }

    private calculateReactions(K: Matrix, displacements: Map<string, NodeDisplacement>, F: Vector, fixedDOFs: number[], nodes: AnalyticalNode[]): Map<string, NodeReaction> {
        const reactions = new Map<string, NodeReaction>();

        // R = K * d - F (for fixed DOFs)
        for (const node of nodes) {
            if (node.boundaryCondition === 'Free') continue;

            const baseIndex = this.nodeIndexMap.get(node.id)! * this.DOF_PER_NODE;
            const reaction: NodeReaction = {
                nodeId: node.id,
                fx: 0, fy: 0, fz: 0,
                mx: 0, my: 0, mz: 0
            };

            // Calculate reactions (simplified)
            for (let i = 0; i < 6; i++) {
                const dofIndex = baseIndex + i;
                if (fixedDOFs.includes(dofIndex)) {
                    let r = -F.get(dofIndex);
                    for (const disp of displacements.values()) {
                        const dispIndex = this.nodeIndexMap.get(disp.nodeId)! * this.DOF_PER_NODE;
                        const dispValues = [disp.dx, disp.dy, disp.dz, disp.rx, disp.ry, disp.rz];
                        for (let j = 0; j < 6; j++) {
                            r += K.get(dofIndex, dispIndex + j) * dispValues[j];
                        }
                    }

                    switch (i) {
                        case 0: reaction.fx = r / 1000; break;
                        case 1: reaction.fy = r / 1000; break;
                        case 2: reaction.fz = r / 1000; break;
                        case 3: reaction.mx = r / 1000; break;
                        case 4: reaction.my = r / 1000; break;
                        case 5: reaction.mz = r / 1000; break;
                    }
                }
            }

            reactions.set(node.id, reaction);
        }

        return reactions;
    }

    private calculateMemberForces(members: AnalyticalMember[], displacements: Map<string, NodeDisplacement>): Map<string, MemberForces> {
        const memberForces = new Map<string, MemberForces>();

        for (const member of members) {
            const startDisp = displacements.get(member.startNodeId);
            const endDisp = displacements.get(member.endNodeId);

            if (!startDisp || !endDisp) continue;

            const startNode = this.modelManager.getModel().nodes.get(member.startNodeId)!;
            const endNode = this.modelManager.getModel().nodes.get(member.endNodeId)!;
            const L = new THREE.Vector3(...startNode.position).distanceTo(new THREE.Vector3(...endNode.position));

            const E = member.material.youngsModulus;
            const A = member.section.area;
            const Ix = member.section.momentOfInertiaX;

            // Simplified internal force calculation
            const numStations = 11;
            const stations: number[] = [];
            const axialForce: number[] = [];
            const shearY: number[] = [];
            const momentY: number[] = [];
            const stress: number[] = [];

            for (let i = 0; i < numStations; i++) {
                const xi = i / (numStations - 1);
                stations.push(xi);

                // Axial strain from displacement difference
                const du = endDisp.dx - startDisp.dx;
                const N = E * A * du / L / 1000;  // kN
                axialForce.push(N);

                // Simple bending (linear moment distribution)
                const dz = endDisp.dz - startDisp.dz;
                const theta = (endDisp.ry + startDisp.ry) / 2;
                const M = E * Ix * 6 * (dz - theta * L) / (L * L) / 1000;  // kN·m
                momentY.push(M * (1 - 2 * xi));

                shearY.push(0);  // Simplified

                // Stress = N/A + M*c/I
                const sigma = Math.abs(N * 1000 / A) + Math.abs(M * 1000 * 0.1 / Ix);  // Pa
                stress.push(sigma / 1e6);  // MPa
            }

            memberForces.set(member.id, {
                memberId: member.id,
                stations,
                axialForce,
                shearY,
                shearZ: Array(numStations).fill(0),
                momentY,
                momentZ: Array(numStations).fill(0),
                torsion: Array(numStations).fill(0),
                stress
            });
        }

        return memberForces;
    }

    private createErrorResult(message: string): AnalysisResults {
        console.error('FEA Error:', message);
        return {
            displacements: new Map(),
            reactions: new Map(),
            memberForces: new Map(),
            maxDisplacement: 0,
            maxStress: 0,
            isConverged: false,
            analysisTime: 0
        };
    }
}
